import React, { useEffect, useState } from "react";
import { Users, Link2, Copy, Trash2, LogOut, Crown, UserMinus, Mail, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { toast } from "sonner";

export default function HouseholdPanel() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [name, setName] = useState("Our household");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/household").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };
  const create = () => run(() => api.post("/household", { name }), "Household created — your bills and debts are now shared here");
  const invite = () => run(async () => {
    const { data: inv } = await api.post("/household/invites", { email: email || null });
    setEmail("");
    await copyLink(inv.code, false);
    toast.success("Invite link created & copied", { description: "Send it to your partner — they sign in with Google and accept." });
  });
  const linkFor = (code) => `${window.location.origin}/join/${code}`;
  const copyLink = async (code, notify = true) => {
    try { await navigator.clipboard.writeText(linkFor(code)); if (notify) toast.success("Invite link copied"); } catch (e) { toast.error("Couldn't copy the link — please copy it manually."); }
  };
  const shareLink = async (inv) => {
    const text = `Join "${inv.household_name}" on OWEverit to manage our bills and debts together: ${linkFor(inv.code)}`;
    if (navigator.share) { try { await navigator.share({ title: "OWEverit household invite", text }); } catch (e) { /* user dismissed the share sheet */ } } else copyLink(inv.code);
  };
  const revoke = (code) => run(() => api.delete(`/household/invites/${code}`), "Invite revoked");
  const removeMember = (m) => { if (window.confirm(`Remove ${m.name} from the household? Their access ends immediately.`)) run(() => api.delete(`/household/members/${m.user_id}`), `${m.name} removed`); };
  const leave = () => {
    const solo = data.members.length === 1;
    if (window.confirm(solo ? "Dissolve the household? Your data stays with you." : "Leave the household? Shared bills and debts stay with the household.")) run(() => api.post("/household/leave"), solo ? "Household dissolved" : "You left the household");
  };

  if (!data) return <div className="h-48 rounded-2xl bg-card border border-border animate-pulse" />;
  const hh = data.household;
  const isOwner = hh && hh.owner_id === user?.user_id;

  return (
    <div className="space-y-4" data-testid="household-panel">
      <p className="text-sm text-muted-foreground">Invite a spouse or partner. Everyone in the household sees and manages the same bills, calendar, debts and payoff plan.</p>

      {!hh ? (
        <div className="p-6 rounded-2xl border border-border bg-card" data-testid="household-create-card">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center"><Users className="h-5 w-5" /></div>
            <div>
              <div className="font-display font-bold text-lg">Start a household</div>
              <div className="text-xs text-muted-foreground">Your current bills and debts become the household's shared data. You can invite people right after.</div>
            </div>
          </div>
          <div className="mt-5 flex gap-2 flex-wrap">
            <input value={name} onChange={e => setName(e.target.value)} data-testid="household-name-input" placeholder="Household name"
              className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
            <button onClick={create} disabled={busy || !name.trim()} data-testid="household-create-btn" className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 disabled:opacity-50">Create household</button>
          </div>
          <div className="mt-4 text-xs text-muted-foreground">Got an invite link from someone? Just open it — you'll join their household.</div>
        </div>
      ) : (
        <>
          <div className="p-6 rounded-2xl border border-border bg-card" data-testid="household-members-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Household</div>
                <div className="font-display font-bold text-2xl" data-testid="household-name">{hh.name}</div>
                <div className="text-xs text-muted-foreground">Since {fmtDate(hh.created_at)} · {data.members.length} member{data.members.length === 1 ? "" : "s"}</div>
              </div>
              <button onClick={leave} disabled={busy} data-testid="household-leave-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm hover:bg-rose-500/10 hover:text-rose-500 hover:border-rose-500/30 transition">
                <LogOut className="h-4 w-4" /> {data.members.length === 1 ? "Dissolve" : "Leave"}
              </button>
            </div>
            <ul className="mt-5 divide-y divide-border/60">
              {data.members.map(m => (
                <li key={m.user_id} className="py-3 flex items-center gap-3" data-testid={`member-${m.user_id}`}>
                  {m.picture ? <img src={m.picture} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center text-xs font-bold">{m.name?.[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2">{m.name}{m.is_owner && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-500 border border-amber-500/30"><Crown className="h-2.5 w-2.5" /> Owner</span>}{m.user_id === user?.user_id && <span className="text-[10px] text-muted-foreground">(you)</span>}</div>
                    <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  </div>
                  {isOwner && m.user_id !== user?.user_id && (
                    <button onClick={() => removeMember(m)} disabled={busy} title="Remove" data-testid={`member-remove-${m.user_id}`} className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-500"><UserMinus className="h-4 w-4" /></button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 rounded-2xl border border-border bg-card" data-testid="household-invite-card">
            <div className="font-display font-bold text-lg flex items-center gap-2"><Link2 className="h-4 w-4 text-indigo-400" /> Invite someone</div>
            <div className="text-xs text-muted-foreground mt-1">Creates a private link. Optionally lock it to an email so only that Google account can accept.</div>
            <div className="mt-4 flex gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="invite-email-input" placeholder="partner@email.com (optional)"
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
              </div>
              <button onClick={invite} disabled={busy} data-testid="invite-create-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 disabled:opacity-50"><Link2 className="h-4 w-4" /> Create invite link</button>
            </div>
            {data.invites.length > 0 && (
              <ul className="mt-5 space-y-2" data-testid="invite-list">
                {data.invites.map(inv => (
                  <li key={inv.code} className="p-3 rounded-xl border border-border/60 bg-background/60 flex items-center gap-3 flex-wrap" data-testid={`invite-${inv.code}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs truncate" data-testid={`invite-link-${inv.code}`}>{linkFor(inv.code)}</div>
                      <div className="text-[11px] text-muted-foreground">{inv.email ? `Locked to ${inv.email}` : "Anyone with the link"} · created {fmtDate(inv.created_at)} · pending</div>
                    </div>
                    <button onClick={() => copyLink(inv.code)} title="Copy link" data-testid={`invite-copy-${inv.code}`} className="p-1.5 rounded-md hover:bg-accent"><Copy className="h-4 w-4" /></button>
                    <button onClick={() => shareLink(inv)} title="Share" data-testid={`invite-share-${inv.code}`} className="p-1.5 rounded-md hover:bg-accent"><Share2 className="h-4 w-4" /></button>
                    <button onClick={() => revoke(inv.code)} title="Revoke" data-testid={`invite-revoke-${inv.code}`} className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-500"><Trash2 className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
